import {
	ReactElement,
	Ref,
	useCallback,
	useRef,
	useState
} from "react";
import { DropOverActionList } from "../DropOverActionList/DropOverActionList";
import styles from "./InlineOptionSelector.module.less";
import {
	convertArbitraryValueForTrackingId,
	generateTrackingId,
	isNotNilOrEmpty,
	setTrackingId
} from "client/Misc/utils";
import { InlineOptionSelectorPreview } from "./InlineOptionSelectorPreview";
import { ActionListItem } from "../ActionList/ActionListItem";

export type InlineOptionSelectorItem<TValue> = {
	text: string;
	value: TValue;
	valueTrackingId?: string;
};

type DropOverAlignment = "left" | "right";

export type InlineOptionSelectorProps<TValue> = {
	value: TValue | undefined;
	onChange(value: TValue): void;
	options: readonly InlineOptionSelectorItem<TValue>[];
	dropOverAlignment?: DropOverAlignment;
	trackingId?: string;
	menuTitle: string;
};

export const InlineOptionSelector = <TValue extends unknown>(
	props: InlineOptionSelectorProps<TValue>
): ReactElement => {
	const {
		value,
		onChange,
		options,
		dropOverAlignment = "left",
		trackingId,
		menuTitle
	} = props;

	const [isOpen, setIsOpen] = useState<boolean>(false);
	const ref: Ref<HTMLButtonElement> = useRef<HTMLButtonElement>(null);

	const previewText: string = options.find(x => x.value === value)?.text ?? "";

	const getItemValueTrackingId = useCallback(
		(item: InlineOptionSelectorItem<TValue>): string => {
			if (isNotNilOrEmpty(item.valueTrackingId)) {
				return item.valueTrackingId;
			}

			return convertArbitraryValueForTrackingId(item.value);
		},
		[]
	);

	return (
		<div
			{...setTrackingId(trackingId)}
			className={styles.root}
		>
			<InlineOptionSelectorPreview
				ref={ref}
				onClick={() => setIsOpen(isOpen => !isOpen)}
				isLeftAligned={dropOverAlignment === "left"}
			>
				{ previewText }
			</InlineOptionSelectorPreview>
			{isOpen && ref.current && (
				<DropOverActionList
					target={ref.current}
					onClose={() => setIsOpen(false)}
					isHeightConstrainted
					trackingId={generateTrackingId(trackingId, "__dropover")}
					targetAlignment={dropOverAlignment === "left" ? "bl" : "br"}
					contentAlignment={dropOverAlignment === "left" ? "tl" : "tr"}
					menuTitle={menuTitle}
				>
					{options.map((item, index) => {
						const isSelected : boolean = item.value === value;

						return (
							<ActionListItem
								key={index}
								isActive={isSelected}
								rightIcon={isSelected ? "check" : undefined}
								text={item.text}
								onClick={() => onChange(item.value)}
								trackingId={generateTrackingId(trackingId, `__item|${getItemValueTrackingId(item)}`)}
							/>
						);
					})}
				</DropOverActionList>
			)}
		</div>
	);
};
InlineOptionSelector.displayName = "InlineOptionSelector";
